import { CSElementPlugin } from '../types/plugin-interfaces';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { WebSocket } from 'ws';

// Типы для fetch API
interface RequestInit {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
}

// Определяем недостающие типы
export interface PluginContext {
    element: CSElement;
    operation: string;
    [key: string]: any;
}

export interface PluginResult {
    success: boolean;
    data?: any;
    error?: string;
    [key: string]: any;
}
import { CSElement } from '../core/CSElement';

// Типы сетевых операций
export enum NetworkOperation {
    SYNC = 'sync',
    UPLOAD = 'upload',
    DOWNLOAD = 'download',
    BROADCAST = 'broadcast',
    SUBSCRIBE = 'subscribe',
    UNSUBSCRIBE = 'unsubscribe',
    PING = 'ping',
    HEARTBEAT = 'heartbeat'
}

export enum ConnectionType {
    HTTP = 'http',
    WEBSOCKET = 'websocket',
    TCP = 'tcp',
    UDP = 'udp',
    GRPC = 'grpc',
    MQTT = 'mqtt'
}

export enum SyncStrategy {
    FULL_SYNC = 'full_sync',
    INCREMENTAL_SYNC = 'incremental_sync',
    CONFLICT_RESOLUTION = 'conflict_resolution',
    MERGE = 'merge',
    OVERWRITE = 'overwrite'
}

export enum NetworkStatus {
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
    CONNECTING = 'connecting',
    RECONNECTING = 'reconnecting',
    ERROR = 'error'
}

export enum ConflictResolution {
    LOCAL_WINS = 'local_wins',
    REMOTE_WINS = 'remote_wins',
    TIMESTAMP_WINS = 'timestamp_wins',
    MERGE_CHANGES = 'merge_changes',
    MANUAL_RESOLUTION = 'manual_resolution'
}

// Интерфейсы сетевых операций
export interface NetworkEndpoint {
    id: string;
    name: string;
    url: string;
    type: ConnectionType;
    headers?: Record<string, string>;
    authentication?: AuthenticationConfig;
    timeout: number;
    retryCount: number;
    retryDelay: number;
    isActive: boolean;
    lastConnected?: Date;
    lastError?: string;
    metadata: Record<string, any>;
}

export interface AuthenticationConfig {
    type: 'none' | 'basic' | 'bearer' | 'apikey' | 'oauth2' | 'custom';
    credentials: Record<string, string>;
    refreshToken?: string;
    expiresAt?: Date;
}

export interface SyncConfiguration {
    id: string;
    name: string;
    endpointId: string;
    strategy: SyncStrategy;
    conflictResolution: ConflictResolution;
    syncInterval: number; // миллисекунды
    enabled: boolean;
    bidirectional: boolean;
    filterRules: SyncFilter[];
    transformRules: SyncTransform[];
    lastSync?: Date;
    nextSync?: Date;
    errorCount: number;
    maxErrors: number;
}

export interface SyncFilter {
    field: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'matches' | 'greater' | 'less';
    value: any;
    include: boolean; // true = include, false = exclude
}

export interface SyncTransform {
    field: string;
    operation: 'rename' | 'map_value' | 'calculate' | 'format' | 'custom';
    parameters: Record<string, any>;
}

export interface NetworkRequest {
    id: string;
    operation: NetworkOperation;
    endpointId: string;
    method: string;
    path: string;
    headers: Record<string, string>;
    body?: any;
    timestamp: Date;
    timeout: number;
    retryCount: number;
    priority: number;
    metadata: Record<string, any>;
}

export interface NetworkResponse {
    requestId: string;
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: any;
    duration: number;
    timestamp: Date;
    error?: string;
}

export interface SyncResult {
    id: string;
    configId: string;
    startTime: Date;
    endTime: Date;
    success: boolean;
    strategy: SyncStrategy;
    elementsProcessed: number;
    elementsUpdated: number;
    elementsCreated: number;
    elementsDeleted: number;
    conflicts: SyncConflict[];
    errors: string[];
    statistics: SyncStatistics;
}

export interface SyncConflict {
    elementId: string;
    field: string;
    localValue: any;
    remoteValue: any;
    resolution: ConflictResolution;
    resolvedValue: any;
    timestamp: Date;
}

export interface SyncStatistics {
    totalElements: number;
    syncedElements: number;
    skippedElements: number;
    conflictedElements: number;
    dataTransferred: number; // байты
    transferSpeed: number; // байт/сек
    networkLatency: number; // миллисекунды
}

export interface NetworkConnection {
    id: string;
    endpointId: string;
    type: ConnectionType;
    status: NetworkStatus;
    connectedAt?: Date;
    lastActivity?: Date;
    messagesSent: number;
    messagesReceived: number;
    bytesTransferred: number;
    latency: number;
    errorCount: number;
    socket?: any; // WebSocket, TCP socket, etc.
}

export interface NetworkMessage {
    id: string;
    connectionId: string;
    type: 'request' | 'response' | 'event' | 'notification';
    operation: NetworkOperation;
    payload: any;
    timestamp: Date;
    priority: number;
    requiresAck: boolean;
    ackReceived?: boolean;
    timeout?: number;
}

export interface NetworkEvent {
    id: string;
    type: 'connection_established' | 'connection_lost' | 'sync_completed' | 'conflict_detected' | 'error_occurred';
    connectionId?: string;
    data: Record<string, any>;
    timestamp: Date;
}

// Конфигурация плагина
export interface NetworkPluginConfig {
    enabled?: boolean;
    priority?: number;
    enableAutoSync: boolean;
    defaultSyncInterval: number;
    maxConcurrentConnections: number;
    connectionTimeout: number;
    requestTimeout: number;
    maxRetries: number;
    retryDelay: number;
    enableOfflineMode: boolean;
    offlineQueueSize: number;
    enableCompression: boolean;
    enableEncryption: boolean;
    heartbeatInterval: number;
    reconnectDelay: number;
    maxReconnectAttempts: number;
    enableEventLogging: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
}

// Статистика сети
export interface NetworkStats {
    totalConnections: number;
    activeConnections: number;
    failedConnections: number;
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    totalDataTransferred: number;
    averageLatency: number;
    averageResponseTime: number;
    syncOperations: number;
    successfulSyncs: number;
    failedSyncs: number;
    conflictsResolved: number;
    offlineOperations: number;
    lastSyncTime?: Date;
    uptime: number;
    errorRate: number;
}

export class NetworkPlugin implements CSElementPlugin {
    public readonly name = 'NetworkPlugin';
    public readonly version = '1.0.0';
    public readonly description = 'Плагин для сетевых операций и синхронизации данных';

    private config: NetworkPluginConfig;
    private endpoints = new Map<string, NetworkEndpoint>();
    private syncConfigs = new Map<string, SyncConfiguration>();
    private connections = new Map<string, NetworkConnection>();
    private pendingRequests = new Map<string, NetworkRequest>();
    private requestQueue: NetworkRequest[] = [];
    private offlineQueue: NetworkRequest[] = [];
    private syncResults: SyncResult[] = [];
    private events: NetworkEvent[] = [];
    private stats: NetworkStats;
    private syncTimer?: NodeJS.Timeout;
    private heartbeatTimer?: NodeJS.Timeout;
    private isOnline = true;

    constructor(config: Partial<NetworkPluginConfig> = {}) {
        this.config = {
            enabled: true,
            priority: 75,
            enableAutoSync: true,
            defaultSyncInterval: 300000, // 5 минут
            maxConcurrentConnections: 10,
            connectionTimeout: 30000,
            requestTimeout: 60000,
            maxRetries: 3,
            retryDelay: 1000,
            enableOfflineMode: true,
            offlineQueueSize: 1000,
            enableCompression: true,
            enableEncryption: false,
            heartbeatInterval: 30000,
            reconnectDelay: 5000,
            maxReconnectAttempts: 5,
            enableEventLogging: true,
            logLevel: 'info',
            ...config
        };

        this.stats = {
            totalConnections: 0,
            activeConnections: 0,
            failedConnections: 0,
            totalRequests: 0,
            successfulRequests: 0,
            failedRequests: 0,
            totalDataTransferred: 0,
            averageLatency: 0,
            averageResponseTime: 0,
            syncOperations: 0,
            successfulSyncs: 0,
            failedSyncs: 0,
            conflictsResolved: 0,
            offlineOperations: 0,
            uptime: 0,
            errorRate: 0
        };
    }

    install(_CSElementClass: typeof CSElement): void {
        // Регистрация плагина в системе CSElement
        // В реальной реализации здесь была бы интеграция с жизненным циклом элементов
        console.log(`${this.name} установлен для CSElement`);
    }

    async initialize(): Promise<void> {
        console.log(`Инициализация ${this.name} v${this.version}`);
        
        // Мониторинг сетевого состояния
        this.setupNetworkMonitoring();
        
        // Запуск автосинхронизации
        if (this.config.enableAutoSync) {
            this.startAutoSync();
        }

        // Запуск heartbeat
        this.startHeartbeat();

        // Обработка очереди запросов
        this.startRequestProcessor();

        // Обновление статистики
        setInterval(() => {
            this.updateStats();
        }, 60000); // каждую минуту
    }

    async destroy(): Promise<void> {
        if (this.syncTimer) {
            clearInterval(this.syncTimer);
        }
        
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
        }

        // Закрытие всех соединений
        for (const connection of this.connections.values()) {
            await this.closeConnection(connection.id);
        }
        
        console.log(`${this.name} деинициализирован`);
    }

    getConfig(): NetworkPluginConfig {
        return { ...this.config };
    }

    updateConfig(newConfig: Partial<NetworkPluginConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }

    // Lifecycle hooks
    async afterCreate(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.config.enableAutoSync) {
            await this.queueElementSync(element, 'create');
        }
        return { success: true };
    }

    async afterUpdate(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.config.enableAutoSync) {
            await this.queueElementSync(element, 'update');
        }
        return { success: true };
    }

    async afterDelete(element: CSElement, _context: PluginContext): Promise<PluginResult> {
        if (this.config.enableAutoSync) {
            await this.queueElementSync(element, 'delete');
        }
        return { success: true };
    }

    // Управление конечными точками
    addEndpoint(endpointData: Omit<NetworkEndpoint, 'id'>): string {
        const endpoint: NetworkEndpoint = {
            id: this.generateId(),
            ...endpointData
        };

        this.endpoints.set(endpoint.id, endpoint);
        this.log('info', `Добавлена конечная точка: ${endpoint.name}`);
        return endpoint.id;
    }

    getEndpoint(id: string): NetworkEndpoint | undefined {
        return this.endpoints.get(id);
    }

    updateEndpoint(id: string, updates: Partial<NetworkEndpoint>): boolean {
        const endpoint = this.endpoints.get(id);
        if (!endpoint) return false;

        Object.assign(endpoint, updates);
        return true;
    }

    deleteEndpoint(id: string): boolean {
        const deleted = this.endpoints.delete(id);
        if (deleted) {
            // Закрытие соединений с этой конечной точкой
            this.closeConnectionsByEndpoint(id);
        }
        return deleted;
    }

    // Управление соединениями
    async createConnection(endpointId: string): Promise<string> {
        const endpoint = this.endpoints.get(endpointId);
        if (!endpoint) {
            throw new Error(`Конечная точка ${endpointId} не найдена`);
        }

        if (this.stats.activeConnections >= this.config.maxConcurrentConnections) {
            throw new Error('Достигнуто максимальное количество соединений');
        }

        const connectionId = this.generateId();
        const connection: NetworkConnection = {
            id: connectionId,
            endpointId,
            type: endpoint.type,
            status: NetworkStatus.CONNECTING,
            messagesSent: 0,
            messagesReceived: 0,
            bytesTransferred: 0,
            latency: 0,
            errorCount: 0
        };

        this.connections.set(connectionId, connection);
        this.stats.totalConnections++;

        try {
            await this.establishConnection(connection, endpoint);
            
            connection.status = NetworkStatus.CONNECTED;
            connection.connectedAt = new Date();
            connection.lastActivity = new Date();
            
            this.stats.activeConnections++;
            this.emitEvent('connection_established', { connectionId, endpointId });
            
            this.log('info', `Установлено соединение с ${endpoint.name}`);
            return connectionId;

        } catch (error) {
            connection.status = NetworkStatus.ERROR;
            connection.errorCount++;
            this.stats.failedConnections++;
            
            this.log('error', `Ошибка подключения к ${endpoint.name}: ${error}`);
            throw error;
        }
    }

    async closeConnection(connectionId: string): Promise<boolean> {
        const connection = this.connections.get(connectionId);
        if (!connection) return false;

        try {
            await this.terminateConnection(connection);
            
            connection.status = NetworkStatus.DISCONNECTED;
            this.stats.activeConnections--;
            
            this.emitEvent('connection_lost', { connectionId });
            this.log('info', `Соединение ${connectionId} закрыто`);
            
            return true;
        } catch (error) {
            this.log('error', `Ошибка при закрытии соединения: ${error}`);
            return false;
        }
    }

    // Конфигурация синхронизации
    addSyncConfig(configData: Omit<SyncConfiguration, 'id' | 'lastSync' | 'nextSync' | 'errorCount'>): string {
        const config: SyncConfiguration = {
            id: this.generateId(),
            lastSync: undefined,
            nextSync: this.calculateNextSync(configData.syncInterval),
            errorCount: 0,
            ...configData
        };

        this.syncConfigs.set(config.id, config);
        this.log('info', `Добавлена конфигурация синхронизации: ${config.name}`);
        return config.id;
    }

    getSyncConfig(id: string): SyncConfiguration | undefined {
        return this.syncConfigs.get(id);
    }

    updateSyncConfig(id: string, updates: Partial<SyncConfiguration>): boolean {
        const config = this.syncConfigs.get(id);
        if (!config) return false;

        Object.assign(config, updates);
        
        if (updates.syncInterval) {
            config.nextSync = this.calculateNextSync(updates.syncInterval);
        }

        return true;
    }

    deleteSyncConfig(id: string): boolean {
        return this.syncConfigs.delete(id);
    }

    // Синхронизация данных
    async syncData(configId: string): Promise<SyncResult> {
        const config = this.syncConfigs.get(configId);
        if (!config) {
            throw new Error(`Конфигурация синхронизации ${configId} не найдена`);
        }

        const endpoint = this.endpoints.get(config.endpointId);
        if (!endpoint) {
            throw new Error(`Конечная точка ${config.endpointId} не найдена`);
        }

        const startTime = new Date();
        const syncResult: SyncResult = {
            id: this.generateId(),
            configId,
            startTime,
            endTime: new Date(),
            success: false,
            strategy: config.strategy,
            elementsProcessed: 0,
            elementsUpdated: 0,
            elementsCreated: 0,
            elementsDeleted: 0,
            conflicts: [],
            errors: [],
            statistics: {
                totalElements: 0,
                syncedElements: 0,
                skippedElements: 0,
                conflictedElements: 0,
                dataTransferred: 0,
                transferSpeed: 0,
                networkLatency: 0
            }
        };

        this.stats.syncOperations++;

        try {
            this.log('info', `Начало синхронизации: ${config.name}`);

            // Получение данных для синхронизации
            const localData = await this.getLocalData(config);
            const remoteData = await this.getRemoteData(endpoint, config);

            syncResult.statistics.totalElements = localData.length + remoteData.length;

            // Выполнение синхронизации
            switch (config.strategy) {
                case SyncStrategy.FULL_SYNC:
                    await this.performFullSync(localData, remoteData, config, syncResult);
                    break;
                
                case SyncStrategy.INCREMENTAL_SYNC:
                    await this.performIncrementalSync(localData, remoteData, config, syncResult);
                    break;
                
                case SyncStrategy.CONFLICT_RESOLUTION:
                    await this.performConflictResolution(localData, remoteData, config, syncResult);
                    break;
                
                default:
                    throw new Error(`Неподдерживаемая стратегия синхронизации: ${config.strategy}`);
            }

            syncResult.success = true;
            syncResult.endTime = new Date();
            
            config.lastSync = new Date();
            config.nextSync = this.calculateNextSync(config.syncInterval);
            config.errorCount = 0;

            this.stats.successfulSyncs++;
            this.stats.lastSyncTime = new Date();
            
            this.emitEvent('sync_completed', { configId, success: true });
            this.log('info', `Синхронизация завершена успешно: ${config.name}`);

        } catch (error) {
            syncResult.success = false;
            syncResult.errors.push(error instanceof Error ? error.message : String(error));
            
            config.errorCount++;
            this.stats.failedSyncs++;
            
            if (config.errorCount >= config.maxErrors) {
                config.enabled = false;
                this.log('warn', `Синхронизация ${config.name} отключена из-за превышения количества ошибок`);
            }

            this.log('error', `Ошибка синхронизации ${config.name}: ${error}`);
        }

        this.syncResults.push(syncResult);
        return syncResult;
    }

    // Отправка запросов
    async sendRequest(request: Omit<NetworkRequest, 'id' | 'timestamp'>): Promise<NetworkResponse> {
        const networkRequest: NetworkRequest = {
            id: this.generateId(),
            timestamp: new Date(),
            ...request
        };

        if (!this.isOnline && this.config.enableOfflineMode) {
            return this.queueOfflineRequest(networkRequest);
        }

        return this.executeRequest(networkRequest);
    }

    // Управление сообщениями
    async sendMessage(connectionId: string, message: Omit<NetworkMessage, 'id' | 'timestamp'>): Promise<boolean> {
        const connection = this.connections.get(connectionId);
        if (!connection || connection.status !== NetworkStatus.CONNECTED) {
            return false;
        }

        const networkMessage: NetworkMessage = {
            id: this.generateId(),
            timestamp: new Date(),
            ...message
        };

        try {
            await this.transmitMessage(connection, networkMessage);
            
            connection.messagesSent++;
            connection.lastActivity = new Date();
            
            return true;
        } catch (error) {
            connection.errorCount++;
            this.log('error', `Ошибка отправки сообщения: ${error}`);
            return false;
        }
    }

    // Утилиты
    private async establishConnection(connection: NetworkConnection, endpoint: NetworkEndpoint): Promise<void> {
        const startTime = Date.now();
        
        try {
            switch (endpoint.type) {
                case ConnectionType.HTTP:
                    // HTTP соединение не требует постоянного подключения
                    connection.status = NetworkStatus.CONNECTED;
                    break;
                
                case ConnectionType.WEBSOCKET:
                    await this.establishWebSocketConnection(connection, endpoint);
                    break;
                
                case ConnectionType.TCP:
                    await this.establishTCPConnection(connection, endpoint);
                    break;
                
                default:
                    throw new Error(`Неподдерживаемый тип соединения: ${endpoint.type}`);
            }

            connection.connectedAt = new Date();
            connection.lastActivity = new Date();
            connection.latency = Date.now() - startTime;
            
            this.emitEvent('connection_established', {
                connectionId: connection.id,
                endpointId: endpoint.id,
                latency: connection.latency
            });

        } catch (error) {
            connection.status = NetworkStatus.ERROR;
            connection.errorCount++;
            
            this.emitEvent('error_occurred', {
                connectionId: connection.id,
                error: error instanceof Error ? error.message : String(error)
            });
            
            throw error;
        }
    }

    private async establishWebSocketConnection(connection: NetworkConnection, endpoint: NetworkEndpoint): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                // Используем динамический импорт для WebSocket в браузере или Node.js
                if (typeof window !== 'undefined' && window.WebSocket) {
                    // Браузерная среда
                    const ws = new WebSocket(endpoint.url);
                    
                    ws.onopen = () => {
                        connection.socket = ws;
                        connection.status = NetworkStatus.CONNECTED;
                        this.setupWebSocketHandlers(connection, ws);
                        resolve();
                    };
                    
                    ws.onerror = (error) => {
                        reject(new Error(`WebSocket connection failed: ${error}`));
                    };
                    
                } else {
                    // Node.js среда
                    const WebSocket = require('ws');
                    const ws = new WebSocket(endpoint.url, {
                        headers: endpoint.headers,
                        handshakeTimeout: endpoint.timeout
                    });
                    
                    ws.on('open', () => {
                        connection.socket = ws;
                        connection.status = NetworkStatus.CONNECTED;
                        this.setupWebSocketHandlers(connection, ws);
                        resolve();
                    });
                    
                    ws.on('error', (error: Error) => {
                        reject(new Error(`WebSocket connection failed: ${error.message}`));
                    });
                }
                
                // Таймаут подключения
                setTimeout(() => {
                    if (connection.status !== NetworkStatus.CONNECTED) {
                        reject(new Error('WebSocket connection timeout'));
                    }
                }, endpoint.timeout);
                
            } catch (error) {
                reject(error);
            }
        });
    }

    private async establishTCPConnection(connection: NetworkConnection, endpoint: NetworkEndpoint): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const net = require('net');
                const url = new URL(endpoint.url);
                const port = parseInt(url.port) || 80;
                const host = url.hostname;
                
                const socket = net.createConnection({ port, host }, () => {
                    connection.socket = socket;
                    connection.status = NetworkStatus.CONNECTED;
                    this.setupTCPHandlers(connection, socket);
                    resolve();
                });
                
                socket.on('error', (error: Error) => {
                    reject(new Error(`TCP connection failed: ${error.message}`));
                });
                
                socket.setTimeout(endpoint.timeout, () => {
                    socket.destroy();
                    reject(new Error('TCP connection timeout'));
                });
                
            } catch (error) {
                reject(error);
            }
        });
    }

    private setupWebSocketHandlers(connection: NetworkConnection, ws: any): void {
        ws.onmessage = (event: any) => {
            connection.messagesReceived++;
            connection.lastActivity = new Date();
            
            try {
                const message = JSON.parse(event.data);
                this.handleIncomingMessage(connection, message);
            } catch (error) {
                this.log('error', `Ошибка парсинга WebSocket сообщения: ${error}`);
            }
        };
        
        ws.onclose = () => {
            connection.status = NetworkStatus.DISCONNECTED;
            this.emitEvent('connection_lost', { connectionId: connection.id });
        };
        
        ws.onerror = (error: any) => {
            connection.errorCount++;
            this.log('error', `WebSocket ошибка: ${error}`);
        };
    }

    private setupTCPHandlers(connection: NetworkConnection, socket: any): void {
        socket.on('data', (data: Buffer) => {
            connection.messagesReceived++;
            connection.bytesTransferred += data.length;
            connection.lastActivity = new Date();
            
            try {
                const message = JSON.parse(data.toString());
                this.handleIncomingMessage(connection, message);
            } catch (error) {
                this.log('error', `Ошибка парсинга TCP сообщения: ${error}`);
            }
        });
        
        socket.on('close', () => {
            connection.status = NetworkStatus.DISCONNECTED;
            this.emitEvent('connection_lost', { connectionId: connection.id });
        });
        
        socket.on('error', (error: Error) => {
            connection.errorCount++;
            this.log('error', `TCP ошибка: ${error.message}`);
        });
    }

    private handleIncomingMessage(connection: NetworkConnection, message: any): void {
        // Обработка входящих сообщений
        this.log('debug', `Получено сообщение от ${connection.id}: ${JSON.stringify(message)}`);
        
        // Здесь можно добавить логику обработки различных типов сообщений
        if (message.type === 'ping') {
            // Отвечаем на ping
            this.sendMessage(connection.id, {
                connectionId: connection.id,
                type: 'response',
                operation: NetworkOperation.PING,
                payload: { pong: true },
                priority: 1,
                requiresAck: false
            });
        }
    }

    private async terminateConnection(connection: NetworkConnection): Promise<void> {
        try {
            connection.status = NetworkStatus.DISCONNECTED;
            
            // Закрываем соединение в зависимости от типа
            if (connection.socket) {
                switch (connection.type) {
                    case ConnectionType.WEBSOCKET:
                        if (connection.socket.readyState === WebSocket.OPEN) {
                            connection.socket.close();
                        }
                        break;
                    case ConnectionType.TCP:
                        if (connection.socket.destroy) {
                            connection.socket.destroy();
                        }
                        break;
                    default:
                        // Для HTTP соединений нет постоянного сокета
                        break;
                }
            }
            
            connection.socket = undefined;
            this.emitEvent('connection_lost', { connectionId: connection.id });
            
        } catch (error) {
            console.error(`Ошибка закрытия соединения ${connection.id}:`, error);
        }
    }

    private async closeConnectionsByEndpoint(endpointId: string): Promise<void> {
        const connectionsToClose = Array.from(this.connections.values())
            .filter(conn => conn.endpointId === endpointId);

        for (const connection of connectionsToClose) {
            await this.closeConnection(connection.id);
        }
    }

    private async queueElementSync(element: CSElement, operation: string): Promise<void> {
        // Поиск подходящих конфигураций синхронизации
        const applicableConfigs = Array.from(this.syncConfigs.values())
            .filter(config => config.enabled && this.shouldSyncElement(element, config));

        for (const config of applicableConfigs) {
            const request: NetworkRequest = {
                id: this.generateId(),
                operation: NetworkOperation.SYNC,
                endpointId: config.endpointId,
                method: 'POST',
                path: '/sync',
                headers: { 'Content-Type': 'application/json' },
                body: {
                    operation,
                    element: element.serialize(),
                    timestamp: new Date().toISOString()
                },
                timestamp: new Date(),
                timeout: this.config.requestTimeout,
                retryCount: 0,
                priority: 1,
                metadata: { configId: config.id, elementId: element.id }
            };

            this.requestQueue.push(request);
        }
    }

    private shouldSyncElement(element: CSElement, config: SyncConfiguration): boolean {
        // Проверка фильтров синхронизации
        return config.filterRules.every(filter => {
            const elementValue = this.getElementValue(element, filter.field);
            const matches = this.evaluateFilter(elementValue, filter.operator, filter.value);
            return filter.include ? matches : !matches;
        });
    }

    private getElementValue(element: CSElement, field: string): any {
        if (field === 'id') return element.id;
        return element.getData(field);
    }

    private evaluateFilter(actual: any, operator: string, expected: any): boolean {
        switch (operator) {
            case 'equals': return actual === expected;
            case 'not_equals': return actual !== expected;
            case 'contains': return String(actual).includes(String(expected));
            case 'matches': return new RegExp(expected).test(String(actual));
            case 'greater': return actual > expected;
            case 'less': return actual < expected;
            default: return true;
        }
    }

    private async executeRequest(request: NetworkRequest): Promise<NetworkResponse> {
        const startTime = Date.now();
        this.stats.totalRequests++;
        this.pendingRequests.set(request.id, request);

        try {
            const endpoint = this.endpoints.get(request.endpointId);
            if (!endpoint) {
                throw new Error(`Endpoint ${request.endpointId} не найден`);
            }

            const url = endpoint.url + request.path;
            
            // Выполняем HTTP запрос с помощью axios
            let axiosResponse;
            
            if (typeof window !== 'undefined') {
                // Браузерная среда - используем fetch
                const fetchOptions: RequestInit = {
                    method: request.method,
                    headers: {
                        ...endpoint.headers,
                        ...request.headers
                    },
                    body: request.body ? JSON.stringify(request.body) : undefined,
                    signal: AbortSignal.timeout(request.timeout)
                };

                // Добавляем аутентификацию
                if (endpoint.authentication) {
                    this.addAuthentication(fetchOptions, endpoint.authentication);
                }

                const fetchResponse = await fetch(url, fetchOptions);
                const responseBody = await fetchResponse.text();
                
                let parsedBody;
                try {
                    parsedBody = JSON.parse(responseBody);
                } catch {
                    parsedBody = responseBody;
                }

                axiosResponse = {
                    status: fetchResponse.status,
                    statusText: fetchResponse.statusText,
                    headers: Object.fromEntries(fetchResponse.headers.entries()),
                    data: parsedBody
                };
            } else {
                // Node.js среда - используем axios
                const axios = require('axios');
                
                const axiosConfig = {
                    method: request.method.toLowerCase(),
                    url,
                    headers: {
                        ...endpoint.headers,
                        ...request.headers
                    },
                    data: request.body,
                    timeout: request.timeout,
                    validateStatus: () => true // Принимаем все статусы
                };

                // Добавляем аутентификацию
                if (endpoint.authentication) {
                    this.addAxiosAuthentication(axiosConfig, endpoint.authentication);
                }

                axiosResponse = await axios(axiosConfig);
            }

            const response: NetworkResponse = {
                requestId: request.id,
                status: axiosResponse.status,
                statusText: axiosResponse.statusText || '',
                headers: axiosResponse.headers || {},
                body: axiosResponse.data,
                duration: Date.now() - startTime,
                timestamp: new Date()
            };

            if (axiosResponse.status >= 200 && axiosResponse.status < 300) {
                this.stats.successfulRequests++;
            } else {
                this.stats.failedRequests++;
                response.error = `HTTP ${axiosResponse.status}: ${axiosResponse.statusText}`;
            }

            this.stats.totalDataTransferred += JSON.stringify(response.body || '').length;
            return response;

        } catch (error) {
            this.stats.failedRequests++;
            
            const response: NetworkResponse = {
                requestId: request.id,
                status: 500,
                statusText: 'Internal Server Error',
                headers: {},
                body: null,
                duration: Date.now() - startTime,
                timestamp: new Date(),
                error: error instanceof Error ? error.message : String(error)
            };

            return response;
        } finally {
            this.pendingRequests.delete(request.id);
        }
    }

    private addAuthentication(options: RequestInit, auth: AuthenticationConfig): void {
        switch (auth.type) {
            case 'basic': {
                const basicAuth = btoa(`${auth.credentials.username}:${auth.credentials.password}`);
                options.headers = {
                    ...options.headers,
                    'Authorization': `Basic ${basicAuth}`
                };
                break;
            }
            
            case 'bearer':
                options.headers = {
                    ...options.headers,
                    'Authorization': `Bearer ${auth.credentials.token}`
                };
                break;
            
            case 'apikey':
                options.headers = {
                    ...options.headers,
                    [auth.credentials.headerName || 'X-API-Key']: auth.credentials.apikey
                };
                break;
        }
    }

    private addAxiosAuthentication(config: any, auth: AuthenticationConfig): void {
        switch (auth.type) {
            case 'basic':
                config.auth = {
                    username: auth.credentials.username,
                    password: auth.credentials.password
                };
                break;
            
            case 'bearer':
                config.headers['Authorization'] = `Bearer ${auth.credentials.token}`;
                break;
            
            case 'apikey':
                config.headers[auth.credentials.headerName || 'X-API-Key'] = auth.credentials.apikey;
                break;
        }
    }

    private async queueOfflineRequest(request: NetworkRequest): Promise<NetworkResponse> {
        if (this.offlineQueue.length >= this.config.offlineQueueSize) {
            this.offlineQueue.shift(); // Удаляем самый старый запрос
        }

        this.offlineQueue.push(request);
        this.stats.offlineOperations++;

        // Возвращаем ответ о том, что запрос поставлен в очередь
        return {
            requestId: request.id,
            status: 202,
            statusText: 'Accepted (Queued for offline processing)',
            headers: {},
            body: { queued: true, position: this.offlineQueue.length },
            duration: 0,
            timestamp: new Date()
        };
    }

    private async getLocalData(config: SyncConfiguration): Promise<any[]> {
        try {
            // Получаем локальные данные из CSElement registry
            const elements: any[] = [];
            
            // В реальной реализации здесь должен быть доступ к реестру элементов
            // Для демонстрации создаем тестовые данные
            for (let i = 0; i < 10; i++) {
                elements.push({
                    id: `local_element_${i}`,
                    data: { value: i, syncConfig: config.id },
                    lastModified: new Date().toISOString()
                });
            }
            
            return elements;
        } catch (error) {
            console.error('Ошибка получения локальных данных:', error);
            return [];
        }
    }

    private async getRemoteData(endpoint: NetworkEndpoint, config: SyncConfiguration): Promise<any[]> {
        try {
            // Выполняем HTTP запрос для получения удаленных данных
            const url = `${endpoint.url}/sync/${config.id}`;
            
            const axiosConfig: AxiosRequestConfig = {
                method: 'GET',
                url,
                headers: endpoint.headers || {},
                timeout: endpoint.timeout,
                validateStatus: () => true
            };

            // Добавляем аутентификацию
            if (endpoint.authentication) {
                this.addAxiosAuthentication(axiosConfig, endpoint.authentication);
            }

            const response: AxiosResponse = await axios(axiosConfig);
            
            if (response.status >= 200 && response.status < 300) {
                return Array.isArray(response.data) ? response.data : [];
            } else {
                console.warn(`Ошибка получения удаленных данных: HTTP ${response.status}`);
                return [];
            }
        } catch (error) {
            console.error('Ошибка получения удаленных данных:', error);
            return [];
        }
    }

    private async performFullSync(
        localData: any[], 
        remoteData: any[], 
        config: SyncConfiguration, 
        result: SyncResult
    ): Promise<void> {
        try {
            // Создаем индексы для быстрого поиска
            const localIndex = new Map(localData.map(item => [item.id, item]));
            const remoteIndex = new Map(remoteData.map(item => [item.id, item]));
            
            // Обрабатываем удаленные элементы
            for (const remoteItem of remoteData) {
                const localItem = localIndex.get(remoteItem.id);
                
                if (!localItem) {
                    // Создаем новый элемент
                    console.log(`Создание элемента: ${remoteItem.id}`);
                    result.elementsCreated++;
                } else {
                    // Обновляем существующий элемент
                    const remoteTime = new Date(remoteItem.lastModified || 0);
                    const localTime = new Date(localItem.lastModified || 0);
                    
                    if (remoteTime > localTime) {
                        console.log(`Обновление элемента: ${remoteItem.id}`);
                        result.elementsUpdated++;
                    }
                }
                
                result.elementsProcessed++;
            }
            
            // Обрабатываем локальные элементы, которых нет на удаленной стороне
            for (const localItem of localData) {
                if (!remoteIndex.has(localItem.id)) {
                    if (config.bidirectional) {
                        // Отправляем локальный элемент на сервер
                        console.log(`Отправка элемента на сервер: ${localItem.id}`);
                        result.elementsCreated++;
                    } else {
                        // Удаляем локальный элемент
                        console.log(`Удаление локального элемента: ${localItem.id}`);
                        result.elementsDeleted++;
                    }
                }
                
                result.elementsProcessed++;
            }
            
            result.statistics.syncedElements = result.elementsProcessed;
            
        } catch (error) {
            result.errors.push(`Ошибка полной синхронизации: ${error}`);
        }
    }

    private async performIncrementalSync(
        localData: any[], 
        remoteData: any[], 
        _config: SyncConfiguration, 
        result: SyncResult
    ): Promise<void> {
        // Заглушка для инкрементальной синхронизации
        result.elementsProcessed = Math.min(localData.length, remoteData.length);
        result.statistics.syncedElements = result.elementsProcessed;
    }

    private async performConflictResolution(
        _localData: any[], 
        _remoteData: any[], 
        _config: SyncConfiguration, 
        result: SyncResult
    ): Promise<void> {
        // Заглушка для разрешения конфликтов
        const conflicts = Math.floor(Math.random() * 5);
        result.statistics.conflictedElements = conflicts;
        this.stats.conflictsResolved += conflicts;
    }

    private async transmitMessage(connection: NetworkConnection, message: NetworkMessage): Promise<void> {
        // Заглушка для отправки сообщения
        const messageSize = JSON.stringify(message).length;
        connection.bytesTransferred += messageSize;
        this.stats.totalDataTransferred += messageSize;
    }

    private setupNetworkMonitoring(): void {
        // Мониторинг состояния сети
        setInterval(() => {
            this.checkNetworkStatus();
        }, 5000); // каждые 5 секунд
    }

    private checkNetworkStatus(): void {
        // Заглушка для проверки состояния сети
        const wasOnline = this.isOnline;
        this.isOnline = true; // navigator.onLine в браузере

        if (!wasOnline && this.isOnline) {
            this.processOfflineQueue();
        }
    }

    private async processOfflineQueue(): Promise<void> {
        this.log('info', `Обработка ${this.offlineQueue.length} отложенных запросов`);
        
        const requests = [...this.offlineQueue];
        this.offlineQueue = [];

        for (const request of requests) {
            try {
                await this.executeRequest(request);
            } catch (error) {
                this.log('error', `Ошибка обработки отложенного запроса: ${error}`);
            }
        }
    }

    private startAutoSync(): void {
        this.syncTimer = setInterval(() => {
            this.runScheduledSyncs();
        }, 60000); // проверка каждую минуту
    }

    private async runScheduledSyncs(): Promise<void> {
        const now = new Date();
        
        for (const config of this.syncConfigs.values()) {
            if (!config.enabled || !config.nextSync) continue;
            
            if (now >= config.nextSync) {
                try {
                    await this.syncData(config.id);
                } catch (error) {
                    this.log('error', `Ошибка автосинхронизации ${config.name}: ${error}`);
                }
            }
        }
    }

    private startHeartbeat(): void {
        this.heartbeatTimer = setInterval(() => {
            this.sendHeartbeats();
        }, this.config.heartbeatInterval);
    }

    private async sendHeartbeats(): Promise<void> {
        for (const connection of this.connections.values()) {
            if (connection.status === NetworkStatus.CONNECTED) {
                try {
                    await this.sendMessage(connection.id, {
                        connectionId: connection.id,
                        type: 'request',
                        operation: NetworkOperation.HEARTBEAT,
                        payload: { timestamp: new Date().toISOString() },
                        priority: 0,
                        requiresAck: false
                    });
                } catch (error) {
                    this.log('warn', `Ошибка heartbeat для соединения ${connection.id}: ${error}`);
                }
            }
        }
    }

    private startRequestProcessor(): void {
        setInterval(() => {
            this.processRequestQueue();
        }, 1000); // каждую секунду
    }

    private async processRequestQueue(): Promise<void> {
        if (this.requestQueue.length === 0) return;

        // Сортировка по приоритету
        this.requestQueue.sort((a, b) => b.priority - a.priority);

        const request = this.requestQueue.shift();
        if (request) {
            try {
                await this.executeRequest(request);
            } catch (error) {
                this.log('error', `Ошибка обработки запроса из очереди: ${error}`);
            }
        }
    }

    private calculateNextSync(interval: number): Date {
        return new Date(Date.now() + interval);
    }

    private emitEvent(type: NetworkEvent['type'], data: Record<string, any>): void {
        if (!this.config.enableEventLogging) return;

        const event: NetworkEvent = {
            id: this.generateId(),
            type,
            data,
            timestamp: new Date()
        };

        this.events.push(event);

        // Ограничение размера лога событий
        if (this.events.length > 1000) {
            this.events = this.events.slice(-1000);
        }
    }

    private updateStats(): void {
        this.stats.uptime = Date.now() - (this.stats.lastSyncTime?.getTime() || Date.now());
        
        if (this.stats.totalRequests > 0) {
            this.stats.errorRate = this.stats.failedRequests / this.stats.totalRequests;
        }

        // Обновление средней задержки
        const activeConnections = Array.from(this.connections.values())
            .filter(conn => conn.status === NetworkStatus.CONNECTED);
        
        if (activeConnections.length > 0) {
            this.stats.averageLatency = activeConnections
                .reduce((sum, conn) => sum + conn.latency, 0) / activeConnections.length;
        }
    }

    private log(level: string, message: string): void {
        if (this.shouldLog(level)) {
            console.log(`[${this.name}] ${level.toUpperCase()}: ${message}`);
        }
    }

    private shouldLog(level: string): boolean {
        const levels = ['debug', 'info', 'warn', 'error'];
        const configLevel = levels.indexOf(this.config.logLevel);
        const messageLevel = levels.indexOf(level);
        return messageLevel >= configLevel;
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    // Публичные методы для получения данных
    getStats(): NetworkStats {
        return { ...this.stats };
    }

    getAllEndpoints(): NetworkEndpoint[] {
        return Array.from(this.endpoints.values());
    }

    getAllSyncConfigs(): SyncConfiguration[] {
        return Array.from(this.syncConfigs.values());
    }

    getActiveConnections(): NetworkConnection[] {
        return Array.from(this.connections.values())
            .filter(conn => conn.status === NetworkStatus.CONNECTED);
    }

    getAllConnections(): NetworkConnection[] {
        return Array.from(this.connections.values());
    }

    getPendingRequests(): NetworkRequest[] {
        return Array.from(this.pendingRequests.values());
    }

    getRequestQueue(): NetworkRequest[] {
        return [...this.requestQueue];
    }

    getOfflineQueue(): NetworkRequest[] {
        return [...this.offlineQueue];
    }

    getSyncResults(): SyncResult[] {
        return [...this.syncResults];
    }

    getEvents(): NetworkEvent[] {
        return [...this.events];
    }

    isNetworkOnline(): boolean {
        return this.isOnline;
    }

    getConnectionStatus(connectionId: string): NetworkStatus | undefined {
        return this.connections.get(connectionId)?.status;
    }
} 